import { Loader2, Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { OrderDetail, useUpdateNoteAttributes } from '@/lib/api/orders';

interface OrderNoteAttributesCardProps {
  order: OrderDetail;
}

type NoteAttribute = {
  key: string;
  value: string | number;
};

export function OrderNoteAttributesCard({ order }: OrderNoteAttributesCardProps) {
  const { t } = useTranslation('orders');
  const updateMutation = useUpdateNoteAttributes();

  const [isEditing, setIsEditing] = React.useState(false);
  const [attributes, setAttributes] = React.useState<NoteAttribute[]>([]);

  // Initialize attributes from order
  React.useEffect(() => {
    const noteAttributes = order.noteAttributes ?? {};
    const attributesList = Object.entries(noteAttributes).map(([key, value]) => ({
      key,
      value,
    }));
    setAttributes(attributesList);
  }, [order.noteAttributes]);

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    // Reset to original values
    const noteAttributes = order.noteAttributes ?? {};
    const attributesList = Object.entries(noteAttributes).map(([key, value]) => ({
      key,
      value,
    }));
    setAttributes(attributesList);
    setIsEditing(false);
  };

  const handleSave = () => {
    // Convert array back to record
    const attributesRecord: Record<string, string | number> = {};
    attributes.forEach((attr) => {
      if (attr.key.trim()) {
        attributesRecord[attr.key.trim()] = attr.value;
      }
    });

    updateMutation.mutate(
      {
        orderId: order.id,
        attributes: attributesRecord,
        merge: false, // Replace all attributes
      },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      },
    );
  };

  const handleAddAttribute = () => {
    setAttributes([...attributes, { key: '', value: '' }]);
  };

  const handleRemoveAttribute = (index: number) => {
    setAttributes(attributes.filter((_, i) => i !== index));
  };

  const handleKeyChange = (index: number, newKey: string) => {
    const newAttributes = [...attributes];
    newAttributes[index] = { ...newAttributes[index], key: newKey };
    setAttributes(newAttributes);
  };

  const handleValueChange = (index: number, newValue: string) => {
    const newAttributes = [...attributes];
    // Try to parse as number if it looks like one
    const parsedValue = /^-?\d+(\.\d+)?$/.test(newValue) ? parseFloat(newValue) : newValue;
    newAttributes[index] = { ...newAttributes[index], value: parsedValue };
    setAttributes(newAttributes);
  };

  const hasAttributes = attributes.length > 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle>{t('noteAttributes')}</CardTitle>
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="ghost" size="sm" onClick={handleCancel} disabled={updateMutation.isPending}>
                <X className="mr-1 h-4 w-4" />
                {t('common:cancel', { ns: 'common' })}
              </Button>
              <Button variant="default" size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-1 h-4 w-4" />
                )}
                {t('common:save', { ns: 'common' })}
              </Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" onClick={handleStartEdit}>
              <Pencil className="mr-1 h-4 w-4" />
              {t('common:edit', { ns: 'common' })}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!hasAttributes && !isEditing ? (
          <div className="text-muted-foreground py-4 text-center text-sm">
            {t('noNoteAttributes')}
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40%]">{t('key')}</TableHead>
                  <TableHead className="w-[50%]">{t('value')}</TableHead>
                  {isEditing && <TableHead className="w-[10%]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {attributes.map((attr, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          value={attr.key}
                          onChange={(e) => handleKeyChange(index, e.target.value)}
                          placeholder={t('keyPlaceholder')}
                          className="h-8"
                        />
                      ) : (
                        <span className="font-medium">{attr.key}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          value={String(attr.value)}
                          onChange={(e) => handleValueChange(index, e.target.value)}
                          placeholder={t('valuePlaceholder')}
                          className="h-8"
                        />
                      ) : (
                        <span className="text-muted-foreground">{String(attr.value)}</span>
                      )}
                    </TableCell>
                    {isEditing && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveAttribute(index)}
                          className="text-destructive hover:text-destructive h-8 w-8"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {attributes.length === 0 && isEditing && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-muted-foreground text-center">
                      {t('noAttributesYet')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
        {isEditing && (
          <div className="mt-4">
            <Button variant="outline" size="sm" onClick={handleAddAttribute} className="w-full">
              <Plus className="mr-1 h-4 w-4" />
              {t('addAttribute')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
